import { useQuery } from "@tanstack/react-query";
import {
  createExtendedResource,
  type ResourceApi,
  type ResourceHooks,
} from "@/core";
import { httpService } from "@/core";
import type { ISettings } from "../types/setting.types";

interface SettingsApi extends ResourceApi<
  ISettings,
  Partial<ISettings>,
  Partial<ISettings>
> {
  getAllBgImages(): Promise<ISettings[]>;
  getSingleBgImage(): Promise<ISettings | null>;
}

interface SettingsHooks extends ResourceHooks<
  ISettings,
  Partial<ISettings>,
  Partial<ISettings>
> {
  // Fix 1: Match generic params - <TData, TError> not <unknown, Error, void>
  useGetAllImages: () => ReturnType<typeof useQuery<ISettings[], Error>>;
  useGetBackgroundImage: () => ReturnType<
    typeof useQuery<ISettings | null, Error>
  >;
}

export const settingsResource = createExtendedResource<
  ISettings,
  Partial<ISettings>,
  Partial<ISettings>,
  SettingsApi,
  SettingsHooks
>(
  {
    endpoint: "/settings",
    queryKey: "settings",
    staleTime: 0, // ← ensure data is considered stale immediately
  },
  (api) => ({
    ...api,
    async getAllBgImages(): Promise<ISettings[]> {
      return httpService.get<ISettings[]>(
        "/settings/bg/all/adminBackgroundImages",
      );
    },
    async getSingleBgImage(): Promise<ISettings | null> {
      return httpService.get<ISettings | null>("/settings/bg-image");
    },
  }),
  (hooks, queryKeys, api) => ({
    ...hooks,
    useGetAllImages: () => {
      return useQuery<ISettings[], Error>({
        queryKey: [...queryKeys.all, "images"],
        // Fix 2: Pass function reference, not invocation
        queryFn: api.getAllBgImages,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
      });
    },
    useGetBackgroundImage: () => {
      return useQuery<ISettings | null, Error>({
        queryKey: [...queryKeys.all, "bg", "image"],
        queryFn: api.getSingleBgImage,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        initialData: null,
      });
    },
  }),
);
